import { ENV_VARS } from '@/env';
import { Col, Divider, Form, Row } from 'antd';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getQuoteProductFooterFields } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import VerticalLine from '../../../../../../components/Bars/VerticalLine';
import Button from '../../../../../../components/Buttons/Button';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import PrimaryLabel from '../../../../../../components/Labels/PrimaryLabel';
import Paper from '../../../../../../components/Paper/Paper';
import FooterContentCard from '../../../../../common/FooterContentCard';
import HallProductDetailsCard from './HallProductDetailsCard';
import { additionalValidationRules } from './constants';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';
const buttonClassName = isHalliburton ? 'hb-button' : '';

const HallProductDetails = () => {
  const { user } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const formItems = Form.useWatch('items', form) || [];
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [footerFields, setFooterFields] = useState([]);

  const { data = {} } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        {
          name: 'items',
          value: data?.quoteDetails?.map((item) => ({
            ...item,
            editMode: true,
          })),
        },
      ]);
    }
  }, []);

  useEffect(() => {
    setFooterFields(getQuoteProductFooterFields(form, t, formItems));
  }, [form, formItems]);

  const isDisabledCopyProductDetailsButton = (items) => {
    return items.length === 0;
  };

  const handleDataChange = (data, index) => {
    const itemsLocal = form.getFieldValue('items');
    itemsLocal[index] = { ...itemsLocal[index], ...data };
    form.setFieldValue('items', itemsLocal);
  };

  const handleCopyLastProductDetails = () => {
    const itemsLocal = form.getFieldValue('items');
    const lastProductDetailsEntry = itemsLocal[itemsLocal.length - 1];
    form.setFieldValue('items', [...itemsLocal, lastProductDetailsEntry]);
  };

  return (
    <Form
      name="productForm"
      form={form}
      initialValues={{
        items: [
          {
            editMode: true,
            isNew: true,
            itemSequenceKey: 1,
            gridOperation: 1,
            createdBy: user?.data?.id,
            billQtyUom: 'PT',
            dimUom: 'N',
            weightUom: 'LB',
          },
        ],
      }}
    >
      <Form.List name="items">
        {(items, { add, remove }) => (
          <>
            <Row justify="space-between" align="middle">
              <Col>
                <PrimaryLabel>{t('translation:getQuote.productDetails.productInformation')}</PrimaryLabel>
              </Col>
              <Col>
                <Row gutter={15}>
                  <Col>
                    <Button
                      dataTestId="product-details-add-product-button"
                      onClick={() => {
                        add({
                          editMode: true,
                          isNew: true,
                          itemSequenceKey: 1,
                          gridOperation: 1,
                          createdBy: user?.data?.id,
                          billQtyUom: 'PT',
                          dimUom: 'N',
                          weightUom: 'LB',
                        });
                      }}
                      className={buttonClassName}
                    >
                      {t('translation:default.buttons.addProduct')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      dataTestId="product-details-copy-last-button"
                      disabled={isDisabledCopyProductDetailsButton(items)}
                      onClick={handleCopyLastProductDetails}
                      className={buttonClassName}
                    >
                      {t('translation:shipment.copyLastProductDetailsEntry')}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider className="border-top-border my-15" style={{ width: '120%', marginLeft: '-10%' }} />
            <Row>
              <HiddenFormInput name="additionalValidation" rules={additionalValidationRules(form)} />
            </Row>
            <div>
              {items.map(({ name }, index) => (
                <HallProductDetailsCard
                  key={name}
                  name={name}
                  index={index}
                  remove={remove}
                  itemDetails={formItems?.[name]}
                  handleDataChange={handleDataChange}
                  isLast={index + 1 === items.length}
                  dataTestId={`product-details-card-${index}`}
                />
              ))}
            </div>

            <div className="mt-15">
              <Paper className={isHalliburton ? 'hb-bg-color px-16 py-5' : 'background-header px-16 py-5'}>
                <Row justify="space-around">
                  {footerFields.map((field, index) => (
                    <Fragment key={field.label}>
                      <Col>
                        <FooterContentCard
                          reverse
                          align="middle"
                          defaultValue="0"
                          count={field.value}
                          label={field.label}
                          contentClass="text-white f-16 fw-600"
                          labelClass="text-light f-14"
                          dataTestId="footer-content-card"
                        />
                      </Col>
                      {index !== footerFields.length - 1 ? (
                        <Col>
                          <VerticalLine />
                        </Col>
                      ) : null}
                    </Fragment>
                  ))}
                </Row>
              </Paper>
            </div>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default memo(HallProductDetails);
